import { ChangeDetectionStrategy, Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  forkJoin,
  map,
  of,
  scan,
  switchMap,
  tap,
  filter,
} from 'rxjs';

import { GlobalBookingTravelClass, LocationRouteCffService, StorageService } from '@fcom/core';
import { LocalDate, TzDate, isEmpty, isPresent, unsubscribe } from '@fcom/utils';
import { BookingAppState } from '@fcom/booking/interfaces';
import { GlobalBookingActions } from '@fcom/common/store';
import { LanguageService } from '@fcom/ui-translate';
import { finShare } from '@fcom/rx';

import { BookingWidgetGtmService } from '../../services/booking-widget-gtm.service';
import { PreviousSearch } from '../../interfaces';
import { PREVIOUS_SEARCHES_KEY } from '../../constants';

@Component({
  selector: 'fin-previous-searches',
  templateUrl: './previous-searches.component.html',
  styleUrls: ['./previous-searches.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviousSearchesComponent implements OnInit, OnDestroy {
  @Output()
  closeLocationModal = new EventEmitter<void>();

  previousSearches$: Observable<PreviousSearch[]>;
  removePreviousSearchIndex$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);

  private loadedPreviousSearches$: Observable<PreviousSearch[]>;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<BookingAppState>,
    private storageService: StorageService,
    private languageService: LanguageService,
    private locationRouteCffService: LocationRouteCffService,
    private bookingWidgetGtmService: BookingWidgetGtmService
  ) {}

  ngOnInit(): void {
    const loadedSearches = this.getPreviousSearches().filter(this.isPreviousSearchValid);

    this.loadedPreviousSearches$ = forkJoin(
      loadedSearches.map((search) =>
        combineLatest([
          this.getLang(search.locale) !== this.languageService.langKeyValue
            ? this.locationRouteCffService.bestGuessFor(search.origin.locationCode, this.languageService.localeValue)
            : of(search.origin),
          this.getLang(search.locale) !== this.languageService.langKeyValue
            ? this.locationRouteCffService.bestGuessFor(
                search.destination.locationCode,
                this.languageService.localeValue
              )
            : of(search.destination),
        ]).pipe(
          map(([origin, destination]) => ({
            ...search,
            origin: {
              ...search.origin,
              cityName: origin.cityName,
            },
            destination: {
              ...search.destination,
              cityName: destination.cityName,
            },
            locale: this.languageService.localeValue,
            tripType: search.tripType ?? search.travelType,
            travelClass: search.travelClass.toUpperCase() as GlobalBookingTravelClass,
          }))
        )
      )
    ).pipe(
      tap((previousSearches) => {
        if (previousSearches.length > 0) {
          this.bookingWidgetGtmService.trackElementEvent(
            'show-previous-searches',
            `previous-searches-${previousSearches.length}`
          );
        }
        this.persistPreviousSearches(previousSearches);
      }),
      finShare()
    );

    this.previousSearches$ = this.removePreviousSearchIndex$.pipe(
      scan<number, number[]>((acc, index) => [...acc, index], []),
      switchMap((indexesToBeRemoved) =>
        this.loadedPreviousSearches$.pipe(
          map((previousSearches) => previousSearches.filter((_, index) => !indexesToBeRemoved.includes(index)))
        )
      ),
      finShare()
    );

    this.subscriptions.add(
      this.previousSearches$.subscribe((previousSearches) => {
        this.persistPreviousSearches(previousSearches);
      })
    );

    this.subscriptions.add(
      this.removePreviousSearchIndex$
        .pipe(
          switchMap((indexToBeRemoved) =>
            this.loadedPreviousSearches$.pipe(
              map((previousSearches) => previousSearches.find((_, index) => indexToBeRemoved === index))
            )
          ),
          filter((previousSearch) => isPresent(previousSearch))
        )
        .subscribe((previousSearch) => {
          this.bookingWidgetGtmService.trackElementEvent(...this.getPreviousSearchContext('remove', previousSearch));
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  setSearch(previousSearch: PreviousSearch): void {
    this.store$.dispatch(GlobalBookingActions.setSelection({ selection: previousSearch }));
    this.bookingWidgetGtmService.trackElementEvent(...this.getPreviousSearchContext('select', previousSearch));
    this.closeLocationModal.emit();
  }

  private getPreviousSearches = (): PreviousSearch[] => {
    const previousSearchesStorage = this.storageService.LOCAL.get(PREVIOUS_SEARCHES_KEY);

    try {
      return JSON.parse(previousSearchesStorage) ?? [];
    } catch {
      return [];
    }
  };

  private persistPreviousSearches = (previousSearches: PreviousSearch[]): void => {
    if (isEmpty(previousSearches)) {
      this.storageService.LOCAL.remove(PREVIOUS_SEARCHES_KEY);
    } else {
      this.storageService.LOCAL.set(PREVIOUS_SEARCHES_KEY, JSON.stringify(previousSearches));
    }
  };

  private isPreviousSearchValid = (search: PreviousSearch): boolean => {
    if (!isPresent(search)) {
      return false;
    }

    const departureIsNotInPast = new LocalDate(search.travelDates.departureDate).gte(LocalDate.now());
    const creationIsBetween14Days =
      Math.floor((TzDate.now().millis - search.creationDate) / (1000 * 60 * 60 * 24)) < 14;

    return departureIsNotInPast && creationIsBetween14Days;
  };

  private getLang = (locale: string): string | undefined => locale?.substring(0, 2);

  private getPreviousSearchContext = (type: string, previousSearch: PreviousSearch): [string, string] => {
    const { origin, destination, travelDates } = previousSearch;
    const locations = `${origin.locationCode} - ${destination.locationCode}`;
    const dates = `DEPARTURE: ${travelDates.departureDate}${
      isPresent(travelDates.returnDate) ? `, RETURN: ${travelDates.returnDate}` : ''
    }`;
    const id = `${type}-previous-search`;
    const state = `${locations}, ${dates}`;

    return [id, state];
  };
}
